import { z } from "zod";

import type { ToolCallDetail } from "../agent-sdk-types.js";
import {
  ToolEditInputSchema,
  ToolEditOutputSchema,
  ToolGlobOutputSchema,
  ToolGrepOutputSchema,
  ToolReadInputSchema,
  ToolReadOutputWithPathSchema,
  ToolSearchInputSchema,
  ToolShellInputSchema,
  ToolShellOutputSchema,
  ToolWebFetchInputSchema,
  ToolWebFetchOutputSchema,
  ToolWebSearchOutputSchema,
  ToolWriteInputSchema,
  ToolWriteOutputSchema,
  extractImageToolDetail,
  toEditToolDetail,
  toFetchToolDetail,
  toReadToolDetail,
  toSearchToolDetail,
  toShellToolDetail,
  toWriteToolDetail,
} from "./tool-call-detail-primitives.js";

type NormalizePathFn = (filePath: string) => string | undefined;

export type ToolDetailOpts = {
  normalizePath?: NormalizePathFn;
};

type RegistryEntry = (
  input: unknown,
  output: unknown,
  opts?: ToolDetailOpts,
) => ToolCallDetail | undefined;

// Loose edit output: try to parse, fall back to null (mirrors CodexLooseEditOutputSchema)
function looseEditOutput(output: unknown) {
  const parsed = ToolEditOutputSchema.safeParse(output);
  return parsed.success ? parsed.data : null;
}

function shellEntry(input: unknown, output: unknown): ToolCallDetail | undefined {
  const parsedInput = ToolShellInputSchema.safeParse(input);
  const parsedOutput = ToolShellOutputSchema.safeParse(output);
  return toShellToolDetail(
    parsedInput.success ? parsedInput.data : null,
    parsedOutput.success ? parsedOutput.data : null,
  );
}

function readEntry(
  input: unknown,
  output: unknown,
  opts?: ToolDetailOpts,
): ToolCallDetail | undefined {
  const parsedInput = ToolReadInputSchema.safeParse(input);
  // Use the WithPath superset so both Claude (content-only) and Codex (path+content) shapes work
  const parsedOutput = ToolReadOutputWithPathSchema.safeParse(output);
  return toReadToolDetail(
    parsedInput.success ? parsedInput.data : null,
    parsedOutput.success ? parsedOutput.data : null,
    opts,
  );
}

function writeEntry(
  input: unknown,
  output: unknown,
  opts?: ToolDetailOpts,
): ToolCallDetail | undefined {
  const parsedInput = ToolWriteInputSchema.safeParse(input);
  const parsedOutput = ToolWriteOutputSchema.safeParse(output);
  return toWriteToolDetail(
    parsedInput.success ? parsedInput.data : null,
    parsedOutput.success ? parsedOutput.data : null,
    opts,
  );
}

const NotebookEditInputSchema = z
  .object({
    notebook_path: z.string(),
    new_source: z.string(),
  })
  .passthrough();

const NotebookEditOutputSchema = z
  .object({
    notebook_path: z.string(),
    new_source: z.string(),
  })
  .passthrough();

function notebookEditEntry(
  input: unknown,
  output: unknown,
  opts?: ToolDetailOpts,
): ToolCallDetail | undefined {
  const parsedInput = NotebookEditInputSchema.safeParse(input);
  const parsedOutput = NotebookEditOutputSchema.safeParse(output);

  const rawPath = parsedInput.success
    ? parsedInput.data.notebook_path
    : parsedOutput.success
      ? parsedOutput.data.notebook_path
      : undefined;
  if (typeof rawPath !== "string") return undefined;
  const trimmedPath = rawPath.trim();
  if (!trimmedPath) return undefined;
  const filePath = opts?.normalizePath ? opts.normalizePath(trimmedPath) : trimmedPath;

  const newSource = parsedInput.success
    ? parsedInput.data.new_source
    : parsedOutput.success
      ? parsedOutput.data.new_source
      : undefined;

  return {
    type: "edit",
    filePath: filePath!,
    ...(newSource ? { newString: newSource } : {}),
  };
}

function editEntry(
  input: unknown,
  output: unknown,
  opts?: ToolDetailOpts,
): ToolCallDetail | undefined {
  const parsedInput = ToolEditInputSchema.safeParse(input);
  return toEditToolDetail(
    parsedInput.success ? parsedInput.data : null,
    looseEditOutput(output),
    opts,
  );
}

function grepEntry(input: unknown, output: unknown): ToolCallDetail | undefined {
  const parsedInput = ToolSearchInputSchema.safeParse(input);
  const parsedOutput = ToolGrepOutputSchema.safeParse(output);
  return toSearchToolDetail({
    input: parsedInput.success ? parsedInput.data : null,
    output: parsedOutput.success ? parsedOutput.data : null,
    toolName: "grep",
  });
}

function globEntry(input: unknown, output: unknown): ToolCallDetail | undefined {
  const parsedInput = ToolSearchInputSchema.safeParse(input);
  const parsedOutput = ToolGlobOutputSchema.safeParse(output);
  return toSearchToolDetail({
    input: parsedInput.success ? parsedInput.data : null,
    output: parsedOutput.success ? parsedOutput.data : null,
    toolName: "glob",
  });
}

function webSearchEntry(input: unknown, output: unknown): ToolCallDetail | undefined {
  const parsedInput = ToolSearchInputSchema.safeParse(input);
  const parsedOutput = ToolWebSearchOutputSchema.safeParse(output);
  return toSearchToolDetail({
    input: parsedInput.success ? parsedInput.data : null,
    output: parsedOutput.success ? parsedOutput.data : null,
    toolName: "web_search",
  });
}

function searchEntry(input: unknown): ToolCallDetail | undefined {
  const parsedInput = ToolSearchInputSchema.safeParse(input);
  return toSearchToolDetail({
    input: parsedInput.success ? parsedInput.data : null,
    toolName: "search",
  });
}

function fetchEntry(input: unknown, output: unknown): ToolCallDetail | undefined {
  const parsedInput = ToolWebFetchInputSchema.safeParse(input);
  const parsedOutput = ToolWebFetchOutputSchema.safeParse(output);
  return toFetchToolDetail(
    parsedInput.success ? parsedInput.data : null,
    parsedOutput.success ? parsedOutput.data : null,
  );
}

const skillInputSchema = z.object({ skill: z.string() }).passthrough();
const skillOutputSchema = z
  .union([
    z
      .object({ output: z.string() })
      .passthrough()
      .transform((v) => v.output),
    z.string(),
  ])
  .nullable();

function skillEntry(input: unknown, output: unknown): ToolCallDetail | undefined {
  const parsedInput = skillInputSchema.safeParse(input);
  if (!parsedInput.success) return undefined;
  const skillName = parsedInput.data.skill;
  if (!skillName) return undefined;
  const parsedOutput = skillOutputSchema.safeParse(output);
  const text = parsedOutput.success ? parsedOutput.data : null;
  return {
    type: "plain_text" as const,
    label: skillName,
    icon: "sparkles" as const,
    ...(text ? { text } : {}),
  } satisfies ToolCallDetail;
}

const taskInputSchema = z
  .object({
    subagent_type: z.string().optional(),
    subAgentType: z.string().optional(),
    description: z.string().optional(),
  })
  .passthrough();

/**
 * Native subagent base card. The `Task` (Claude) / `task` (OpenCode) tool spawns
 * a subagent; this entry produces the static `sub_agent` shell (type + description)
 * so the SubAgentCard renders immediately. The live action log is layered on top
 * by the provider's sidechain tracker, which emits richer `sub_agent` updates
 * sharing the same callId. The merge layer keeps those actions/log fields when
 * this actionless base card arrives — see `mergeToolCallDetail`.
 */
function taskEntry(input: unknown): ToolCallDetail | undefined {
  const parsed = taskInputSchema.safeParse(input);
  if (!parsed.success) return undefined;
  const subAgentType = (parsed.data.subagent_type ?? parsed.data.subAgentType)?.trim();
  const description = parsed.data.description?.trim();
  return {
    type: "sub_agent" as const,
    ...(subAgentType ? { subAgentType } : {}),
    ...(description ? { description } : {}),
  } satisfies ToolCallDetail;
}

const speakInputSchema = z
  .union([z.string().transform((text) => ({ text })), z.object({ text: z.string() }).passthrough()])
  .nullable();

function speakEntry(input: unknown): ToolCallDetail | undefined {
  const parsedInput = speakInputSchema.safeParse(input);
  const text = parsedInput.success ? parsedInput.data?.text?.trim() : undefined;
  if (!text) return undefined;
  return { type: "unknown" as const, input: text, output: null };
}

/**
 * Superset of every tool-name alias across all providers. One entry here = rendered
 * identically by Claude, Codex, and OpenCode. Add a new alias here, all providers
 * get it for free.
 */
export const TOOL_DETAIL_REGISTRY: Record<string, RegistryEntry> = {
  // --- Shell ---
  Bash: shellEntry,
  bash: shellEntry,
  shell: shellEntry,
  exec: shellEntry,
  exec_command: shellEntry,
  command: shellEntry,
  // --- Read ---
  Read: readEntry,
  read: readEntry,
  read_file: readEntry,
  view_file: readEntry,
  // --- Write ---
  Write: writeEntry,
  write: writeEntry,
  write_file: writeEntry,
  create_file: writeEntry,
  // --- Edit ---
  Edit: editEntry,
  MultiEdit: editEntry,
  multi_edit: editEntry,
  edit: editEntry,
  NotebookEdit: notebookEditEntry,
  apply_patch: editEntry,
  apply_diff: editEntry,
  str_replace_editor: editEntry,
  // --- Grep (typed output) ---
  Grep: grepEntry,
  grep: grepEntry,
  // --- Glob (typed output) ---
  Glob: globEntry,
  glob: globEntry,
  // --- Web search ---
  WebSearch: webSearchEntry,
  web_search: webSearchEntry,
  websearch: webSearchEntry,
  // --- Generic search ---
  search: searchEntry,
  // --- Web fetch ---
  WebFetch: fetchEntry,
  web_fetch: fetchEntry,
  WebFetchTool: fetchEntry,
  web_fetch_tool: fetchEntry,
  webfetch: fetchEntry,
  // --- Subagent (Task) → sub_agent base card; sidechain tracker adds the live log ---
  Task: taskEntry,
  task: taskEntry,
  // --- Skill → plain_text card ---
  Skill: skillEntry,
  skill: skillEntry,
  // --- speak ---
  speak: speakEntry,
};

/**
 * Shared tool-detail deriver consumed by all provider parsers.
 *
 * 1. Runs `extractImageToolDetail` as a mandatory pre-pass — image content blocks
 *    (MCP screenshots, Playwright, Photoshop, Blender…) render as images for
 *    EVERY provider, regardless of tool name.
 * 2. Looks up the tool name in `TOOL_DETAIL_REGISTRY`.
 * 3. Falls back to `unknown`.
 *
 * `opts.normalizePath` carries the Codex cwd-stripping function; pass `undefined`
 * for Claude/OpenCode where no path normalisation is needed.
 */
export function deriveToolDetail(
  name: string,
  input: unknown,
  output: unknown,
  opts?: ToolDetailOpts,
): ToolCallDetail {
  // Pre-pass: image blocks win over any name-based dispatch
  const imageDetail = extractImageToolDetail(name, output);
  if (imageDetail) return imageDetail;

  const entry = TOOL_DETAIL_REGISTRY[name];
  if (entry) {
    const detail = entry(input, output, opts);
    if (detail) return detail;
  }

  return {
    type: "unknown",
    input: input ?? null,
    output: output ?? null,
  };
}
